import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3aa7433b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "date-range-container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "and-separator" }, "and", -1))

import { computed, ref, watch, onMounted, PropType } from "vue";
import FieldValidationFreeDateBox from "@/components/ui/inputs/field-validation-free-date-box.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import SingleSelect from "@/components/ui/dropdowns/single-select.vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { computedStartDate, computedEndDate } from "@/utils/ComputedDates";
import { DateRanges } from "@/enums/DateRanges";
import { CurrentUser } from "@/types/user/CurrentUser";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
import { CountryCode } from "bright-logic-core/src/CountryCode";


export default /*#__PURE__*/_defineComponent({
  __name: 'date-ranges-input',
  props: {
  /**
   * The end date of the date range.
   * @type {string}
   * @required: true
   */
  endDate: {
    type: String,
    required: true,
  },
  /**
   * The start date of the date range.
   * @type {string}
   * @required: true
   */
  startDate: {
    type: String,
    required: true,
  },
  /**
   * The title or heading associated with the component.
   * @type {string}
   * @default: ''
   */
  title: {
    type: String,
    default: "",
  },
  /**
   * The custom date Range Options.
   * @type Array as PropType<IDDescriptionPair[]>
   * @default: () => []
   */
  customRangeOptions: {
    type: Array as PropType<IDDescriptionPair[]>,
    default: () => [],
  },
  /**
   * The default date Range
   * @type Number
   * @default: DateRanges.CustomRange
   */
  defaultRange: {
    type: Number as PropType<DateRanges>,
    default: DateRanges.CustomRange,
  },
  /**
   * Determines whether the specific control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The date box width
   * @type String as PropType<FieldSize>
   * @default FieldSize.Small
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Small,
  },
  /**
   * Determines whether the control implemented by accounts.
   * @type {boolean}
   * @default false
   */
  fromAccounts: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Emitted when the endDate value changes, passing a formatted string (ISO or custom).
   * Typically used with v-model.
   * @event update:endDate
   * @type {string}
   */
  "update:endDate",
  /**
   * Emitted when the startDate value changes, passing a formatted string (ISO or custom).
   * Typically used with v-model.
   * @event update:startDate
   * @type {string}
   */
  "update:startDate",
  /**
   * Emitted when the dateRangeOptionChanged value changes, passing a formatted string (ISO or custom).
   * Typically used with v-model.
   * @event update:dateRangeOptionChanged
   * @type {string}
   */
  "dateRangeOptionChanged",
],
  setup(__props, { emit }) {

const props = __props;





const startDateFilter = computed({
  get: () => props.startDate,
  set: (value: string) => emit("update:startDate", value),
});

const endDateFilter = computed({
  get: () => props.endDate,
  set: (value: string) => emit("update:endDate", value),
});

const currentUser = ref<CurrentUser>();
const currentUserStore = useCurrentUserStore();
const dateRangeSelected = ref(props.defaultRange);
const disableDateBoxes = ref(false);

onMounted(async () => {
  if (props.customRangeOptions.length > 0) {
    props.customRangeOptions.forEach((range) => {
      rangeOptions.value.push(range);
    });
  }

  // Get Current User details from the Current User store
  currentUser.value = await currentUserStore.getCurrentUserDetails();

  setDatesBasedOnSelectedRanged();
});

// If a user has enterd an end date, they can't be allowed to enter a start date that's later than the end date.
const startDateMax = computed(() => {
  return props.endDate
    ? new Date(props.endDate)
    : new Date(new Date().setFullYear(new Date().getFullYear() + 50));
});

// If a user has enterd a start date, they can't be allowed to enter an end date that's earlier than the start date.
const endDateMin = computed(() => {
  return props.startDate
    ? new Date(props.startDate)
    : new Date(new Date().setFullYear(new Date().getFullYear() - 50));
});

const rangeOptions = computed<IDDescriptionPair[]>(() => {
  return [
    { id: DateRanges.CustomRange, description: "Custom Range" },
    { id: DateRanges.AllDates, description: "All Dates" },
    { id: DateRanges.Today, description: "Today" },
    { id: DateRanges.ThisWeek, description: "This Week" },
    { id: DateRanges.ThisMonth, description: "This Month" },
    { id: DateRanges.ThisYear, description: "This Year" },
    ...(props.fromAccounts
      ? [
          {
            id: DateRanges.ThisFinancialYear,
            description: "This Financial Year",
          },
          { id: DateRanges.ThisVATYear, description: "This VAT Year" },
        ]
      : []),
    ...(props.fromAccounts &&
    currentUser.value?.countryCodeID === CountryCode.UK
      ? [{ id: DateRanges.ThisHMRCTaxYear, description: "This HMRC Tax Year" }]
      : []),
    { id: DateRanges.Yesterday, description: "Yesterday" },
    { id: DateRanges.LastWeek, description: "Last Week" },
    { id: DateRanges.LastMonth, description: "Last Month" },
    { id: DateRanges.LastYear, description: "Last Year" },
    ...(props.fromAccounts
      ? [
          {
            id: DateRanges.LastFinancialYear,
            description: "Last Financial Year",
          },
        ]
      : []),
    ...(props.fromAccounts &&
    currentUser.value?.countryCodeID === CountryCode.UK
      ? [{ id: DateRanges.LastHMRCTaxYear, description: "Last HMRC Tax Year" }]
      : []),
    ...(props.fromAccounts
      ? [
          {
            id: DateRanges.LastVATYear,
            description: "Last VAT Year",
          },
          {
            id: DateRanges.ThisVATQuarter,
            description: "This VAT Quarter",
          },
          {
            id: DateRanges.LastVATQuarter,
            description: "Last VAT Quarter",
          },
          {
            id: DateRanges.LastMonthLastYear,
            description: "Last Month Last Year",
          },
        ]
      : []),
    { id: DateRanges.TwoMonths, description: "Two Months Ago" },
    { id: DateRanges.Last30Days, description: "Last 30 Days" },
    { id: DateRanges.Last60Days, description: "Last 60 Days" },
    { id: DateRanges.Last90Days, description: "Last 90 Days" },
  ];
});

watch(
  () => dateRangeSelected.value,
  () => {
    const selectedItem = rangeOptions.value.find(
      (x) => x.id === dateRangeSelected.value,
    );
    emit("dateRangeOptionChanged", dateRangeSelected.value, selectedItem);

    setDatesBasedOnSelectedRanged();
  },
);

const setDatesBasedOnSelectedRanged = () => {
  if (
    dateRangeSelected.value == DateRanges.CustomRange &&
    !props.fromAccounts
  ) {
    disableDateBoxes.value = false;
  } else {
    disableDateBoxes.value = true;
    startDateFilter.value = computedStartDate(dateRangeSelected.value);
    endDateFilter.value = computedEndDate(dateRangeSelected.value);
  }
  if (dateRangeSelected.value == DateRanges.CustomRange && props.fromAccounts) {
    disableDateBoxes.value = false;
  }
};

watch(
  () => props.defaultRange,
  () => {
    dateRangeSelected.value = props.defaultRange;
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("span", null, _toDisplayString(props.title), 1),
    _createElementVNode("section", _hoisted_1, [
      _createVNode(SingleSelect, {
        dataSource: rangeOptions.value,
        modelValue: dateRangeSelected.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRangeSelected).value = $event)),
        readOnly: props.readOnly
      }, null, 8, ["dataSource", "modelValue", "readOnly"]),
      _createVNode(FieldValidationFreeDateBox, {
        modelValue: startDateFilter.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((startDateFilter).value = $event)),
        maxDate: startDateMax.value,
        readOnly: disableDateBoxes.value || props.readOnly
      }, null, 8, ["modelValue", "maxDate", "readOnly"]),
      _hoisted_2,
      _createVNode(FieldValidationFreeDateBox, {
        modelValue: endDateFilter.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((endDateFilter).value = $event)),
        minDate: endDateMin.value,
        readOnly: disableDateBoxes.value || props.readOnly
      }, null, 8, ["modelValue", "minDate", "readOnly"])
    ])
  ], 64))
}
}

})